import { Icon } from '@eventbrite/eds-icon';
import { CalendarChunky } from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import React, { useEffect, useState } from 'react';
import type { Event, SeriesDatesScopedProps } from '../../../../types';
import { getUTCTimezone } from '../../../utils/localizedDates';
import {
    Detail,
    DetailContent,
    DetailHeading,
    DetailIcon,
    ICON_SIZE,
} from '../../../_shared/Detail/Detail';
import { ChildEventsDropdown } from './ChildEventsDropdown';
import { ChildEventsSlider } from './ChildEventsSlider';
import './DateAndTime.scss';

export type DateAndTimeProps = {
    displayDate: string;
    displayEndDateWithTz: string;
    displayStartDateWithTz: string;
    doorTime: string | null;
    doorTimeLineOne?: string;
    doorTimeLineTwo?: string;
    doorTimeLineThree?: string;
    dateLineTwo: string;
    dateLineOne: string;
    hideStartDate: boolean;
    hideEndDate: boolean;
    isSameStartEndDate: boolean;
    shouldShowDoorTime: string | null;
    childEventsDropdown: SeriesDatesScopedProps;
    shouldDisplayCrawlableSeriesChildrenLinks: boolean;
    isRepeatingChildEventsSliderExperiment: boolean;
    localeInfo: { locale: string };
} & Pick<Event, 'childEvents' | 'start'>;

export const DateAndTime = ({
    childEvents,
    childEventsDropdown,
    displayDate,
    displayEndDateWithTz,
    displayStartDateWithTz,
    dateLineTwo,
    dateLineOne,
    hideStartDate,
    hideEndDate,
    isSameStartEndDate,
    shouldShowDoorTime,
    doorTime,
    doorTimeLineOne,
    doorTimeLineTwo,
    doorTimeLineThree,
    isRepeatingChildEventsSliderExperiment,
    start: eventStart,
    localeInfo: { locale: eventLocale },
}: DateAndTimeProps) => {
    const [timezone, setTimezone] = useState('');
    const { isOnlineEvent } = childEventsDropdown;

    useEffect(() => {
        const timezone = getUTCTimezone({
            date: eventStart,
            isOnlineEvent,
            locale: eventLocale,
            isRepeatingChildEventsSliderExperiment,
        });
        timezone && setTimezone(timezone);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!displayDate) {
        return null;
    }

    const LABEL_ID = 'date-time-heading';
    let separator = '';

    const formattedDateTime = () => {
        const shouldShowDoorTimeLineTwo =
            (!isSameStartEndDate && !hideEndDate) || hideStartDate;
        const shouldRenderComma =
            isSameStartEndDate && !hideEndDate && !hideStartDate && !doorTime;
        const shouldRenderSpace = !isSameStartEndDate;
        if (shouldRenderComma) {
            separator = ', ';
        } else if (shouldRenderSpace) {
            separator = ' ';
        }

        const MusicEventDateTime = () => (
            <>
                {doorTimeLineOne}
                {separator}
                <span className="date-and-time__line-break">
                    {shouldShowDoorTimeLineTwo ? doorTimeLineTwo : ''}
                </span>
                {doorTimeLineThree && `, ${doorTimeLineThree}`}
            </>
        );
        const DefaultEventDateTime = () => (
            <>
                {dateLineOne}
                {separator}
                <span className="date-and-time__line-break">{dateLineTwo}</span>
            </>
        );

        return shouldShowDoorTime ? (
            <MusicEventDateTime />
        ) : (
            <DefaultEventDateTime />
        );
    };

    const DateTimeLines = () => (
        <>
            <p className="date-and-time">
                <meta
                    content={displayStartDateWithTz}
                    data-testid="event-start-date-tz"
                />
                <meta
                    content={displayEndDateWithTz}
                    data-testid="event-end-date-tz"
                />
                <time>{formattedDateTime()}</time>
            </p>
        </>
    );

    const shouldShowChildEvents = childEvents && childEvents.length > 0;

    return (
        <section aria-labelledby={LABEL_ID}>
            <Detail>
                <DetailIcon>
                    <Icon
                        type={<CalendarChunky />}
                        size={ICON_SIZE}
                        isBlockLevel={true}
                    />
                </DetailIcon>
                <DetailContent>
                    <DetailHeading>
                        <h3 id={LABEL_ID}>{gettext('Date and time')}</h3>
                    </DetailHeading>
                    <DateTimeLines />
                    {shouldShowChildEvents ? (
                        <>
                            {isRepeatingChildEventsSliderExperiment ? (
                                <p>
                                    {timezone && `All in ${timezone} timezone`}
                                </p>
                            ) : (
                                <ChildEventsDropdown {...childEventsDropdown} />
                            )}
                        </>
                    ) : null}
                </DetailContent>
            </Detail>
            {shouldShowChildEvents && isRepeatingChildEventsSliderExperiment ? (
                <ChildEventsSlider {...childEventsDropdown} />
            ) : null}
        </section>
    );
};
